import "core-js/modules/es.array.push.js";
// 常量定义
const DEFAULT_VIEW_BOX_SIZE = 1000; // SVG 视图大小，使用1000便于计算和缩放
const DEFAULT_COLOR = '#1989fa'; // 默认前景色
const DEFAULT_LAYER_COLOR = '#fff'; // 默认背景色
const DEFAULT_SIZE = {
  width: 46,
  height: 46
}; // 默认大小
const DEFAULT_CLOCKWISE = true; // 默认旋转方向为顺时针
const DEFAULT_STROKE_WIDTH = 68; // 默认描边宽度

export default {
  name: 'om-circle-progress',
  props: {
    // 当前时间（秒）
    currentTime: {
      type: [Number, String],
      default: 0,
      validator: value => parseFloat(value) >= 0
    },
    // 总时长（秒）
    duration: {
      type: [Number, String],
      default: 15,
      validator: value => parseFloat(value) > 0
    },
    // 是否自动开始动画
    autoStart: {
      type: Boolean,
      default: false
    },
    // 是否使用动画过渡
    useTransition: {
      type: Boolean,
      default: false
    },
    // 进度环大小
    size: {
      type: Object,
      default: () => DEFAULT_SIZE
    },
    // 前景色
    color: {
      type: [String, Object],
      default: DEFAULT_COLOR
    },
    // 背景色
    layerColor: {
      type: String,
      default: DEFAULT_LAYER_COLOR
    },
    // 旋转方向
    clockwise: {
      type: Boolean,
      default: DEFAULT_CLOCKWISE
    },
    // 描边宽度
    strokeWidth: {
      type: [Number, String],
      default: DEFAULT_STROKE_WIDTH
    },
    // 背景描边宽度
    layerStrokeWidth: {
      type: [Number, String],
      default: DEFAULT_STROKE_WIDTH
    },
    // 描边线帽样式
    strokeLinecap: {
      type: String,
      default: 'round'
    }
  },
  data() {
    return {
      svgViewBoxSize: DEFAULT_VIEW_BOX_SIZE,
      animationState: 'stopped',
      // running, paused, stopped
      animationFrame: null,
      startTime: null,
      pausedAt: null,
      elapsedTime: 0,
      // 已经过去的时间（秒）
      currentProgress: 0,
      lastUpdateTime: null
    };
  },
  computed: {
    style() {
      const {
        width,
        height
      } = this.size;
      return {
        width: `${+width}px`,
        height: `${+height}px`
      };
    },
    /**
     * 计算 SVG 视图的中心点坐标
     */
    svgCenter() {
      return this.svgViewBoxSize / 2;
    },
    /**
     * 计算圆环半径
     */
    radius() {
      return this.svgViewBoxSize / 2 * 0.95;
    },
    /**
     * 生成 SVG 路径
     */
    path() {
      const clockwise = this.clockwise ? 1 : 0;
      const center = this.svgCenter;
      const r = this.radius;
      const path = [`M ${center} ${center}`, `m 0, -${r}`, `a ${r}, ${r} 0 1, ${clockwise} 0, ${r * 2}`, `a ${r}, ${r} 0 1, ${clockwise} 0, -${r * 2}`];
      return path.join(' ');
    },
    pathStyle() {
      return {
        stroke: `${this.layerColor}`,
        strokeWidth: `${this.layerStrokeWidth}px`,
        strokeLinecap: this.strokeLinecap
      };
    },
    conicGradient() {
      if (typeof this.color !== 'object') {
        return null;
      }
      // 将对象格式转换为数组格式
      const stops = [];
      Object.keys(this.color).forEach(key => {
        stops.push({
          offset: key,
          color: this.color[key]
        });
      });
      return stops;
    },
    perimeter() {
      return 2 * Math.PI * this.radius;
    },
    strokeDashoffset() {
      return this.perimeter * (1 - this.currentProgress) + 'px';
    }
  },
  mounted() {
    if (this.autoStart) {
      this.$nextTick(() => {
        this.start();
      });
    }
  },
  methods: {
    /**
     * 开始或恢复动画
     */
    start() {
      const validTime = Math.max(0, parseFloat(this.currentTime) || 0);
      const duration = parseFloat(this.duration);

      // 记录上次更新时间
      this.lastUpdateTime = Date.now();
      // 更新进度
      this.currentProgress = Math.min(1, validTime / duration);
      this.elapsedTime = this.currentProgress * parseFloat(this.duration) * 1000;
      if (this.animationState === 'running') {
        return;
      }
      // 如果是从暂停状态恢复
      if (this.animationState === 'paused') {
        this.resume();
        return;
      }
      // 开始新的动画
      this.startTime = Date.now();
      this.animationState = 'running';
      // 发送开始事件
      this.$emit('start', this.getProgressData());
      // 开始动画循环
      this.animate();
    },
    /**
     * 暂停动画
     */
    pause() {
      if (this.animationState !== 'running') {
        return;
      }
      this.pausedAt = Date.now();
      this.elapsedTime += this.pausedAt - this.startTime;
      this.clearAnimation();
      this.animationState = 'paused';

      // 发送暂停事件
      this.$emit('pause', this.getProgressData());
    },
    /**
     * 恢复动画
     */
    resume() {
      if (this.animationState !== 'paused') {
        return;
      }
      this.startTime = Date.now();
      this.animationState = 'running';

      // 发送恢复事件
      this.$emit('resume', this.getProgressData());

      // 继续动画
      this.animate();
    },
    /**
     * 重置动画
     */
    reset() {
      this.clearAnimation();
      this.currentProgress = 0;
      this.elapsedTime = 0;
      this.animationState = 'stopped';

      // 发送重置事件
      this.$emit('reset', this.getProgressData());
    },
    /**
     * 从当前时间继续动画
     */
    continueFromCurrentTime() {
      this.clearAnimation();
      this.startTime = Date.now();
      this.elapsedTime = this.currentProgress * parseFloat(this.duration) * 1000;
      if (this.animationState === 'running') {
        this.animate();
      }
    },
    /**
     * 动画帧函数
     */
    animate() {
      if (this.animationState !== 'running') {
        return;
      }
      const now = Date.now();
      const deltaTime = now - this.startTime;
      const totalElapsed = this.elapsedTime + deltaTime;
      const duration = parseFloat(this.duration) * 1000;
      // 计算当前进度
      this.currentProgress = Math.min(1, totalElapsed / duration);

      // 发送进度更新事件
      this.$emit('update', this.getProgressData());

      // 检查是否完成
      if (totalElapsed >= duration) {
        this.handleComplete();
      } else {
        this.animationFrame = requestAnimationFrame(this.animate);
      }
    },
    /**
     * 处理动画完成
     */
    handleComplete() {
      this.clearAnimation();
      this.currentProgress = 1;
      this.animationState = 'stopped';

      // 发送完成事件
      this.$emit('complete', this.getProgressData());
    },
    /**
     * 清除动画
     */
    clearAnimation() {
      if (this.animationFrame) {
        cancelAnimationFrame(this.animationFrame);
        this.animationFrame = null;
      }
    },
    /**
     * 获取当前进度数据
     */
    getProgressData() {
      const duration = parseFloat(this.duration);
      const currentTime = this.currentProgress * duration;
      return {
        currentTime: parseFloat(currentTime.toFixed(1)),
        duration: duration,
        progress: parseFloat((this.currentProgress * 100).toFixed(1)),
        remaining: parseFloat((duration - currentTime).toFixed(1)),
        state: this.animationState
      };
    }
  }
};